import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import { resetFilters, saveFilters, toggleMenu } from 'actions/dashboardPhone';
import { useActivityUpdater } from 'hooks/activityUpdater';
import moment from 'moment';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import queryString from 'query-string';
import { filtersToParams } from 'selectors/app';
import API from 'services/API';
import strings from 'strings';
import LineChart from 'UI/components/molecules/Dashboard/LineChart';
import StatsWrapper from 'UI/components/molecules/Dashboard/StatsWrapper';
import SummaryTable from 'UI/components/molecules/Dashboard/SummaryTable';
import SummaryTotals from 'UI/components/molecules/Dashboard/SummaryTotals';
import ContentPageLayout from 'UI/components/templates/ContentPageLayout';
import FiltersLayout from 'UI/components/templates/FiltersLayout';
import { additionalPeriodRanges } from 'UI/components/templates/SideFiltersLayout/filters';
import TabsView from 'UI/components/templates/TabsView';
import { backNavigateListConfig } from 'UI/constants/config';
import {
  DateFormats,
  PageTitles,
  RingCentralLogTypes,
  WebSocketNamespaces
} from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { createCustomStaticRanges, getRangePeriod, objectToArray } from 'UI/utils';

import { useStyles } from '../styles';

import { CallSummaryContainer } from './components';

/** @typedef {import('types/dashboard').Filters} Filters */
const FiltersGroups = [{ id: 'team', name: 'Team', filters: ['coach', 'recruiter'] }];
const {
  title,
  sections: {
    noPhoneActivity: {
      noNewCalls: noNewCallsLabel,
      noNewTexts: noNewTextsLabel,
      title: noPhoneActivityTitle
    },
    totalCalls,
    callTrends,
    calls,
    totalTexts,
    textTrends,
    texts
  }
} = strings.phoneDashboard;

/**
 * @param {Filters} filters
 */
const determineScope = filters => {
  const options = {
    option1: 'gpac All',
    option2: 'All Coaches'
  };

  if (!filters.coach && !filters.recruiter && !filters.regional) return options;
  if (filters.recruiter && filters.recruiter?.value) {
    const recruiterScope = filters.recruiter.value.full_name;
    return {
      option1: recruiterScope,
      option2: recruiterScope
    };
  }
  if (filters.coach?.value) {
    const coachScope = `Team ${filters.coach.value.full_name}`;
    return {
      option1: coachScope,
      option2: coachScope
    };
  }

  if (filters.regional?.value) {
    const regionScope = `Region ${filters.regional.value.full_name}`;
    return {
      option1: regionScope,
      option2: regionScope
    };
  }
  return {};
};

const TABS = {
  calls: 0,
  texts: 1
};

const COPIES = strings.phoneDashboard.sections.callSummaryTable;

const CALL_LABELS = {
  outbound: COPIES.footer.outboundCalls,
  inbound: COPIES.footer.inboundCalls,
  missed: COPIES.footer.missedCalls,
  notConnected: COPIES.footer.notConnectedCalls
};

/**
 * @param {Object} props
 * @param {Filters} props.filters
 * @param {boolean} props.isSideMenuOpen
 * @param {Function} props.onFiltersReset
 * @param {Function} props.onFiltersChange
 * @param {Function} props.onMenuToggle
 */
const DashboardPhone = ({
  filters,
  isSideMenuOpen,
  onFiltersReset,
  onFiltersChange,
  onMenuToggle
}) => {
  const classes = useStyles();

  const defaultPeriod = getRangePeriod([1, 'week']);
  const [selectedPeriod, setSelectedPeriod] = useState(defaultPeriod);
  const [selectedTab, setSelectedTab] = useState(0);
  const [callLastUpdate, setCallLastUpdate] = useState();
  const [messageLastUpdate, setMessageLastUpdate] = useState();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const isTotalCallsFeatureFlagEnabled = checkIfFeatureFlagEnabled(
    FeatureFlags.PhoneDashboardTotalCalls
  );

  useEffect(() => {
    document.title = PageTitles.DashboardPhone;
    getLastUpdates();
  }, []);

  const handlePeriodChange = period => setSelectedPeriod(period);
  const handleTabChange = (event, newValue = 0) => setSelectedTab(newValue);

  const filtersParams = filtersToParams(filters);
  const queryParams = queryString.stringify({
    startDate: moment(selectedPeriod.startDate).format(DateFormats.QueryShortFormat),
    endDate: moment(selectedPeriod.endDate).format(DateFormats.QueryShortFormat),
    ...filtersParams
  });

  const scope = determineScope(filters);

  const getLastUpdates = async () => {
    const { data: lastUpdates } = await API.get(`${Endpoints.Phone}/logs-last-update`);
    const getDate = logType =>
      lastUpdates.find(update => update.rcLogTypeId === logType?.id)?.dateLastUpdate;

    setCallLastUpdate(getDate(RingCentralLogTypes.Call));
    setMessageLastUpdate(getDate(RingCentralLogTypes.Text));
  };

  const handleMessageSocket = useCallback(async message => {
    if (!message) return;
    getLastUpdates();
  }, []);

  useActivityUpdater(WebSocketNamespaces.DashboardPhoneLoading, handleMessageSocket);

  const noPhoneActivityTabsProp = [
    {
      label: noNewCallsLabel,
      view: (
        <SummaryTable
          url={`${Endpoints.Phone}/calls-inventory-list?${queryParams}&withoutItems=true`}
          downloadFileName={`No-Inventory-${scope.option2}`}
          section="dashboardPhone"
          lastUpdate={callLastUpdate}
        />
      )
    },
    {
      label: noNewTextsLabel,
      view: (
        <SummaryTable
          url={`${Endpoints.Phone}/sms-inventory-list?${queryParams}&withoutItems=true`}
          downloadFileName={`No-Activity-${scope.option2}`}
          section="dashboardPhone"
          lastUpdate={messageLastUpdate}
        />
      )
    }
  ];

  const tabsViewFooterProps = {
    [TABS.calls]: {
      callLastUpdate
    },
    [TABS.texts]: {
      textLastUpdate: messageLastUpdate
    }
  };

  const stats = [
    {
      child: (
        <SummaryTotals
          url={`${Endpoints.Phone}/calls/summary-time?${queryParams}`}
          lastUpdate={callLastUpdate}
        />
      ),
      shouldShow: isTotalCallsFeatureFlagEnabled,
      shouldShowFooter: true,
      FooterProps: {
        callLastUpdate,
        detailText: COPIES.footer.summaryTime
      },
      title: totalCalls.title
    },
    {
      child: (
        <CallSummaryContainer url={`${Endpoints.Phone}/calls/summary-totals?${queryParams}`} />
      ),
      shouldShow: isTotalCallsFeatureFlagEnabled,
      contentPadding: 'bottom',
      shouldShowFooter: true,
      FooterProps: {
        callLastUpdate,
        detailText: [COPIES.footer.ringCentralDisclaimer, ...objectToArray(CALL_LABELS)].join(
          '<br>'
        )
      },
      title: totalCalls.title
    },
    {
      child: (
        <SummaryTotals
          url={`${Endpoints.Phone}/total-calls-inventory?${queryParams}`}
          lastUpdate={callLastUpdate}
        />
      ),
      shouldShow: !isTotalCallsFeatureFlagEnabled,
      shouldShowFooter: true,
      title: totalCalls.title
    },
    {
      child: (
        <LineChart
          url={`${Endpoints.Phone}/calls-activity-in-time?${queryParams}`}
          columns={[]}
          lastUpdate={callLastUpdate}
        />
      ),
      shouldShow: true,
      shouldShowFooter: true,
      title: callTrends.title
    },
    {
      child: (
        <SummaryTable
          url={`${Endpoints.Phone}/calls-inventory-list?${queryParams}`}
          downloadFileName={`Inventory-CallsRC-${scope.option2}`}
          section="dashboardPhone"
          lastUpdate={callLastUpdate}
        />
      ),
      contentPadding: 'bottom',
      shouldShow: !filters.recruiter,
      shouldShowFooter: true,
      FooterProps: {
        callLastUpdate,
        type: 'table'
      },
      subheader: scope.option2,
      title: calls.title
    },
    {
      child: (
        <SummaryTotals
          url={`${Endpoints.Phone}/calls-inventory-list-by-recruiter?${queryParams}`}
          lastUpdate={callLastUpdate}
        />
      ),
      shouldShow: filters.recruiter,
      shouldShowFooter: true,
      subheader: scope.option2,
      title: calls.title
    },
    {
      child: (
        <SummaryTotals
          url={`${Endpoints.Phone}/total-sms-inventory?${queryParams}`}
          lastUpdate={messageLastUpdate}
        />
      ),
      contentPadding: 'bottom',
      shouldShow: true,
      shouldShowFooter: true,
      FooterProps: {
        textLastUpdate: messageLastUpdate
      },
      title: totalTexts.title
    },
    {
      child: (
        <LineChart
          url={`${Endpoints.Phone}/sms-activity-in-time?${queryParams}`}
          columns={[]}
          lastUpdate={messageLastUpdate}
        />
      ),
      shouldShow: true,
      shouldShowFooter: true,
      FooterProps: {
        textLastUpdate: messageLastUpdate
      },
      title: textTrends.title
    },
    {
      child: (
        <SummaryTable
          url={`${Endpoints.Phone}/sms-inventory-list?${queryParams}`}
          downloadFileName={`Inventory-SMSRC-${scope.option2}`}
          section="dashboardPhone"
          lastUpdate={messageLastUpdate}
        />
      ),
      contentPadding: 'bottom',
      shouldShow: !filters.recruiter,
      shouldShowFooter: true,
      FooterProps: {
        textLastUpdate: messageLastUpdate,
        type: 'table'
      },
      title: texts.title
    },
    {
      child: (
        <TabsView
          selectedTab={selectedTab}
          onChangeTabIndex={handleTabChange}
          tabs={noPhoneActivityTabsProp}
          panelHeight="unset"
        />
      ),
      shouldShow: !filters.recruiter,
      shouldShowFooter: true,
      contentPadding: 'bottom',
      FooterProps: {
        ...tabsViewFooterProps[selectedTab],
        subheader: scope.option2,
        type: 'table'
      },
      title: noPhoneActivityTitle
    }
  ];

  const statsCommonProps = {
    lastUpdate: callLastUpdate,
    scope,
    FooterProps: {
      callLastUpdate
    }
  };

  return !callLastUpdate && !messageLastUpdate ? (
    <ContentPageLayout>
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    </ContentPageLayout>
  ) : (
    <FiltersLayout
      dateRangerPickerProps={{
        staticRanges: createCustomStaticRanges(additionalPeriodRanges)
      }}
      defaultRange={selectedPeriod}
      disableTimeRange
      filters={filters}
      groups={FiltersGroups}
      isSideMenuOpen={isSideMenuOpen}
      onFiltersChange={onFiltersChange}
      onFiltersReset={onFiltersReset}
      onMenuToggle={onMenuToggle}
      onPeriodChange={handlePeriodChange}
      section="dashboardPhone"
      title={title}
      titleLabelProps={backNavigateListConfig}
    >
      <Grid container spacing={3}>
        <StatsWrapper {...statsCommonProps} stats={stats} />
      </Grid>
    </FiltersLayout>
  );
};

const mapStateToProps = ({ dashboardPhone }) => {
  return {
    filters: dashboardPhone.domain.filters,
    isSideMenuOpen: dashboardPhone.ui.isSideMenuOpen
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onFiltersReset: () => dispatch(resetFilters()),
    onFiltersChange: filters => dispatch(saveFilters(filters)),
    onMenuToggle: () => dispatch(toggleMenu())
  };
};

const DashboardPhoneConnected = connect(mapStateToProps, mapDispatchToProps)(DashboardPhone);

export default DashboardPhoneConnected;
